/**
 * patientFieldLimits.ts
 *
 * ScriptSure's length limits for the patient fields Prospera sends it, applied
 * while staff type demographics. DAW's patient-demographics page makes this a
 * SureScripts certification requirement ("enforce limits in your UI at
 * data-entry time"), and no value may be shortened for the user: they must be
 * asked to abbreviate it. So these limits never truncate. An over-length
 * value is flagged on its field and blocks the save.
 *
 * The numbers match DawPrescribingService::MAX_LENGTHS on the server, which
 * still checks every chart open for rows written by other screens. The
 * patients columns are varchar(50), so these only bind on DAW accounts.
 */

export const SCRIPTSURE_PATIENT_FIELD_LIMITS = {
  firstName: 35,
  middleName: 35,
  lastName: 35,
  address: 40,
  city: 35,
} as const;

export type LimitedPatientField = keyof typeof SCRIPTSURE_PATIENT_FIELD_LIMITS;

/**
 * One message per field whose trimmed value is over its limit; empty when all
 * fit. The server trims before it measures, so this does too.
 */
export function patientFieldLimitErrors(
  values: Partial<Record<LimitedPatientField, string | null | undefined>>,
): Partial<Record<LimitedPatientField, string>> {
  const errors: Partial<Record<LimitedPatientField, string>> = {};
  for (const field of Object.keys(SCRIPTSURE_PATIENT_FIELD_LIMITS) as LimitedPatientField[]) {
    const limit = SCRIPTSURE_PATIENT_FIELD_LIMITS[field];
    const length = (values[field] ?? '').trim().length;
    if (length > limit) {
      errors[field] = `Prescriptions allow up to ${limit} characters here. Please shorten or abbreviate it (now ${length}).`;
    }
  }
  return errors;
}

const FIELD_NAMES: Record<LimitedPatientField, string> = {
  firstName: 'first name',
  middleName: 'middle name',
  lastName: 'last name',
  address: 'address',
  city: 'city',
};

/**
 * One line naming every field still over its limit, for a place that stays in
 * view (the editor's footer), so a refused Save never looks like a dead
 * button. Null when nothing is over.
 */
export function patientFieldLimitSummary(
  errors: Partial<Record<LimitedPatientField, string>>,
): string | null {
  const names = (Object.keys(SCRIPTSURE_PATIENT_FIELD_LIMITS) as LimitedPatientField[])
    .filter((field) => errors[field])
    .map((field) => FIELD_NAMES[field]);
  if (names.length === 0) return null;
  const list = names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  return `Shorten the ${list} before saving. Prescriptions allow fewer characters.`;
}
